import { readApiError } from '../lib/errors/readApiError';

export { translateApiMessage } from '../lib/errors/readApiError';

export interface ApiErrorShape {
  /** Translated and safe to render. Callers may put this straight on screen. */
  message: string;
  /**
   * The raw backend value behind `message`, before translation, for the rare caller that
   * branches on a specific key instead of displaying it. Absent when the reason did not
   * come from the response body.
   */
  messageKey?: string;
  status?: number;
}

/**
 * Narrow view of {@link readApiError} kept for the display sites that only ever wanted the
 * sentence and the status. All parsing lives in `lib/errors/readApiError`; this holds none.
 *
 * `fieldErrors` and `isAuthorization` are deliberately dropped rather than passed through:
 * a caller that needs either is asking a question this shape cannot answer, and should read
 * `readApiError` directly.
 */
export function extractApiError(e: unknown): ApiErrorShape {
  const { message, messageKey, status } = readApiError(e);
  return { message, messageKey, status };
}

/**
 * The translated, human-readable reason for a failed request.
 *
 * `fallbackKey` names the sentence to show when the response carried no reason of its own.
 * This used to be written at the call site as `getErrorMessage(e) || t('...')`, which never
 * fired — the left side is always a non-empty string, so the contextual key was unreachable
 * and the user got the per-status generic sentence instead.
 *
 * Use in catch blocks as: `setError(getErrorMessage(e, 'product.form.error.save'))`
 */
export function getErrorMessage(e: unknown, fallbackKey?: string): string {
  return readApiError(e, fallbackKey).message;
}
